import { setPaused } from "@stores";
import { MathUtils } from "@utils/math";
import { EventDispatcher, Quaternion, Vector3 } from "three";
class PointerLockControls extends EventDispatcher {
    phi;
    phiSpeed;
    theta;
    thetaSpeed;
    sensitivity;
    rotation;
    domElement;
    isLocked;
    changeEvent;
    lockEvent;
    unlockEvent;
    retryInterval;
    static instance;
    /**
     * A Pointer Lock controls object.
     */
    constructor() {
        super();
        this.phi = 0;
        this.phiSpeed = 8;
        this.theta = 0;
        this.thetaSpeed = 5;
        this.sensitivity = 0.0002;
        this.rotation = new Quaternion();
        this.domElement = document.body;
        this.isLocked = false;
        this.changeEvent = { type: "change" };
        this.lockEvent = { type: "lock" };
        this.unlockEvent = { type: "unlock" };
        this.retryInterval = 500;
        this.connect();
    }
    /**
     * Lock the pointer lock controls.
     */
    lock() {
        this.domElement.requestPointerLock();
    }
    /**
     * Unlock the pointer lock controls.
     */
    unlock() {
        this.domElement.ownerDocument.exitPointerLock();
    }
    /**
     * Add all event listeners associated with pointer lock controls.
     * @private
     */
    connect() {
        this.domElement.ownerDocument.addEventListener("mousemove", event => this.onMouseMove(event));
        this.domElement.ownerDocument.addEventListener("pointerlockchange", () => this.onPointerLockChange());
        this.domElement.ownerDocument.addEventListener("pointerlockerror", () => this.onPointerLockError());
    }
    /**
     * Handle mouse movement.
     * @param event the mouse movement event.
     * @private
     */
    onMouseMove(event) {
        if (!this.isLocked)
            return;
        const xh = event.movementX * this.sensitivity || 0;
        const yh = event.movementY * this.sensitivity || 0;
        this.phi += -xh * this.phiSpeed;
        this.theta = MathUtils.clamp(-Math.PI / 3, this.theta + -yh * this.thetaSpeed, Math.PI / 3);
        const qx = new Quaternion();
        qx.setFromAxisAngle(new Vector3(0, 1, 0), this.phi);
        const qz = new Quaternion();
        qz.setFromAxisAngle(new Vector3(1, 0, 0), this.theta);
        const rotation = new Quaternion();
        rotation.multiply(qx);
        rotation.multiply(qz);
        this.rotation.copy(rotation);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.dispatchEvent(this.changeEvent);
    }
    /**
     * Handle pointer lock controls change.
     * @private
     */
    onPointerLockChange() {
        if (this.domElement.ownerDocument.pointerLockElement === this.domElement) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.dispatchEvent(this.lockEvent);
            this.isLocked = true;
            setPaused(false);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.dispatchEvent(this.unlockEvent);
            this.isLocked = false;
            setPaused(true);
        }
    }
    /**
     * Handle pointer lock controls errors.
     * @private
     */
    onPointerLockError() {
        console.log(`Unable to use Pointer Lock API.\nTrying again in ${MathUtils.cutDecimals(this.retryInterval / 1000, 10, "trunc")} seconds.`);
        setTimeout(() => this.lock(), this.retryInterval);
    }
    get getRotation() {
        return this.rotation;
    }
    get getIsLocked() {
        return this.isLocked;
    }
    static getInstance() {
        if (!PointerLockControls.instance)
            PointerLockControls.instance = new PointerLockControls();
        return PointerLockControls.instance;
    }
}
export { PointerLockControls };
